import { injectIntl } from 'react-intl';
import css from './MultiCheckBox.scss';
import { CheckBox, formatMessage, IconPath, Image, TriggerField } from 'cw-ui-core';
import Locale from 'src/i18n';
import cx from 'classnames';
import React, { useState, useEffect } from 'react';

const MultiCheckBox = props => {
    const [displayText, setDisplayText] = useState('');
    const [openCategories, setOpenCategories] = useState([]);
    const [showOnlySelected, setShowOnlySelected] = useState(false);
    const [showOnlyUnselected, setShowOnlyUnselected] = useState(false);

    useEffect(() => {
        if (
            ((props.values.length === 0 && props.values.length > 0) || (displayText !== '' && displayText === '')) &&
            props.subItemsField
        ) {
            const openCategories = [];
            props.items.forEach(category => {
                if (categoryContainsValue(category)) {
                    openCategories.push(category.name);
                }
            });

            onUpdate(openCategories);
        }
    }, [props.values, displayText, props.subItemsField, props.items]);

    const onUpdate = openCategories => {
        setOpenCategories(openCategories);
    };

    const handleChange = enteredText => {
        setDisplayText(enteredText);
        setOpenCategories(enteredText === '' ? [] : getCategoryValues());
    };

    const getShowOnlySelected = () => {
        return (
            <CheckBox
                className={css.checkBox}
                key={`${props.pathKey}-showOnlySelected`}
                text={formatMessage(Locale.showOnlySelected)}
                style={{ marginTop: 8, fontStyle: 'italic' }}
                value={showOnlySelected}
                onChange={e => {
                    setShowOnlySelected(e);
                    setShowOnlyUnselected(false);
                }}
            />
        );
    };

    const getShowOnlyUnselected = () => {
        return (
            <CheckBox
                className={css.checkBox}
                key={`${props.pathKey}-showAllUnselected`}
                text={formatMessage(Locale.showOnlyUnselected)}
                style={{ fontStyle: 'italic' }}
                value={showOnlyUnselected}
                onChange={e => {
                    setShowOnlyUnselected(e);
                    setShowOnlySelected(false);
                }}
            />
        );
    };

    const getSelectAll = () => {
        return (
            <CheckBox
                className={css.checkBox}
                key={`${props.pathKey}-selectAll`}
                text={formatMessage(Locale.selectAll)}
                style={{ marginTop: props.showFilters ? 0 : 8 }}
                value={props.selectedAll}
                onChange={() => {
                    props.onSelectAll(props.selectedAll);
                }}
            />
        );
    };

    const getCategoryValues = () => {
        const categories = [];
        props.items.forEach(item => {
            categories.push(item.name);
        });
        return categories;
    };

    const toggleCategory = (name, open) => {
        const updatedOpenCategories = [...openCategories];

        if (open) {
            const index = updatedOpenCategories.indexOf(name);
            delete updatedOpenCategories[index];
        } else {
            updatedOpenCategories.push(name);
        }

        setOpenCategories(updatedOpenCategories);
    };

    const getCategory = (name, displayName, description, open) => {
        return (
            <div
                className={css.category}
                key={name}
                role="menuitem"
                tabIndex="0"
                onClick={() => {
                    toggleCategory(name, open);
                }}>
                <Image src={open ? IconPath.get('Action_Tree_Collapse') : IconPath.get('Action_Tree_Expand')} />
                {displayName}
                {description ? (
                    <Image className={css.help} src={IconPath.get('Application_Help')} title={description} />
                ) : null}
            </div>
        );
    };

    const getCategories = () => {
        if (!props.items) {
            return null;
        }

        const renderItems = [];
        if (displayText === '' && !props.disableSelectAll) {
            renderItems.push(getSelectAll());
        }

        props.items.forEach(item => {
            const isOpen = openCategories.includes(item.name);
            const subItems = getItems(item[props.subItemsField]);
            if (subItems.length > 0) {
                renderItems.push(getCategory(item.name, item.displayName, item.description, isOpen));
            }
            if (isOpen) {
                renderItems.push(getItems(item[props.subItemsField]));
            }
        });

        return renderItems;
    };

    const getItems = items => {
        if (!items) {
            return null;
        }
        const filteredItems = items.filter(item => shouldItemRender(item, displayText));

        let checkboxes = [];

        if (props.showFilters) {
            checkboxes.push(getShowOnlySelected());
            checkboxes.push(getShowOnlyUnselected());
        }

        if (!props.subItemsField && !props.disableSelectAll && displayText === '') {
            checkboxes.push(getSelectAll());
        }
        const disableValues = props.disableValues && props.disableValues.length > 0 ? props.disableValues : [];

        const getTitle = item => {
            if (props.showHoverOnFull) {
                return props.customDescription;
            }
            if (props.customDescription) {
                return props.customDescription(item) ?? item.description;
            }
            return '';
        };

        checkboxes = checkboxes.concat(
            filteredItems.reduce((accumulator, item) => {
                let disablecheckBox = false;
                if (disableValues.includes(item.name)) {
                    disablecheckBox = true;
                }
                // Disable all checkboxes if the disableValues array contains only '*'
                if (disableValues.length === 1 && disableValues[0] === '*') {
                    disablecheckBox = true;
                }
                const itemId = props.customId ? props.customId(item) : item.id;
                const itemName = props.customName ? props.customName(item) : item.name;
                if (
                    (!showOnlySelected && !showOnlyUnselected) ||
                    (showOnlySelected && containsValue(itemId)) ||
                    (showOnlyUnselected && !containsValue(itemId))
                ) {
                    accumulator.push(
                        <div
                            title={getTitle(item)}
                            className={props.showHoverOnFull ? cx(css.item, css.itemHover) : css.item}
                            key={`${itemId}-wrap`}>
                            <CheckBox
                                className={props.subItemsField ? css.subItemCheckBox : css.checkBox}
                                key={itemId}
                                text={itemName}
                                style={{
                                    marginTop: checkboxes.length === 0 && accumulator.length === 0 ? 8 : 0,
                                }}
                                value={containsValue(itemId)}
                                onChange={() => {
                                    props.onChange(item);
                                }}
                                disabled={disablecheckBox}
                            />
                            {props.showDescriptions && item.description ? (
                                <Image
                                    className={css.help}
                                    key={`${item.id}-help`}
                                    style={{
                                        marginBottom: checkboxes.length === 0 && accumulator.length === 0 ? 0 : 8,
                                    }}
                                    src={IconPath.get('Application_Help')}
                                    title={item.description}
                                />
                            ) : null}
                        </div>
                    );
                }
                return accumulator;
            }, [])
        );

        return checkboxes;
    };

    const categoryContainsValue = category => {
        const containsValue = props.values.some(value => {
            const index = category[props.subItemsField].findIndex(subItem => {
                return subItem.name === value;
            });
            return index !== -1;
        });

        return containsValue;
    };

    const shouldItemRender = (item, enteredText) => {
        if (enteredText === '') {
            return true;
        }
        const itemName = props.customName ? props.customName(item) : item.name;
        return itemName.toLowerCase().includes(enteredText.toLowerCase());
    };

    const containsValue = id => {
        return props.values.includes(id);
    };

    return (
        <>
            <TriggerField
                className={css.search}
                customElement={
                    <Image
                        style={{ position: 'absolute', top: '-4px', left: '-6px' }}
                        src={IconPath.get('Action_Search')}
                    />
                }
                onChange={handleChange}
                value={displayText}
            />
            <div className={css.wrap} style={props.style}>
                {props.subItemsField ? getCategories() : getItems(props.items)}
            </div>
        </>
    );
};

export default injectIntl(MultiCheckBox);
